import React, { useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import MenuDrawer from './MenuDrawer';
import BookingDrawer from './BookingDrawer';
import AuthModal from './AuthModal';

// Palace Silhouette Accent Component
const PalaceSilhouette = () => (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden -z-10">
    <img
      src="/hawa_mahal_silhouette.jpg"
      alt="Hawa Mahal Palace Silhouette Jaipur"
      className="w-full h-full object-cover opacity-[0.025] mix-blend-multiply"
      loading="lazy"
      decoding="async"
    />
  </div>
);

export default function LegalPage({
  view,
  setView,
  isMenuOpen,
  setIsMenuOpen,
  isCartOpen,
  setIsCartOpen,
  isBookingOpen,
  setIsBookingOpen,
  isSearchOpen,
  setIsSearchOpen,
  favorites,
  setFavorites,
  cartItems,
  setCartItems,
  bookingStep,
  setBookingStep,
  bookingForm,
  setBookingForm,
  title, // 'Shipping Policy' | 'Exchange, Return & Refund Policy' | 'Terms & Conditions' | 'Privacy Policy'
  setLegalTitle,
  currentUser,
  setCurrentUser,
}) {
  const [isAuthModalOpen, setIsAuthModalOpen] = React.useState(false);

  // Normalize active policy title
  const activeTitle = (title === 'Refund Policy' || title === 'Exchange & Return Policy')
    ? 'Exchange, Return & Refund Policy'
    : (title || 'Shipping Policy');

  const policiesList = [
    {
      id: 'shipping',
      name: 'Shipping Policy',
      shortName: 'Shipping Policy',
      path: '/shipping-policy',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.25h2.25c.621 0 1.125.504 1.125 1.125v4.5" />
        </svg>
      )
    },
    {
      id: 'return',
      name: 'Exchange, Return & Refund Policy',
      shortName: 'Exchange & Return',
      path: '/return-policy',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
        </svg>
      )
    },
    {
      id: 'terms',
      name: 'Terms & Conditions',
      shortName: 'Terms & Conditions',
      path: '/terms-and-conditions',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      )
    },
    {
      id: 'privacy',
      name: 'Privacy Policy',
      shortName: 'Privacy Policy',
      path: '/privacy-policy',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751A11.959 11.959 0 0112 2.714z" />
        </svg>
      )
    }
  ];

  const handleTabClick = (policy) => {
    setLegalTitle(policy.name);
    window.history.pushState(null, '', policy.path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTitle]);

  return (
    <div className="min-h-screen bg-noise text-[#584738] font-sans flex flex-col antialiased selection:bg-[#B59E7D]/30 selection:text-[#584738]">
      
      {/* HEADER */}
      <Header
        setView={setView}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        setIsSearchOpen={setIsSearchOpen}
        setIsAuthOpen={setIsAuthModalOpen}
        setIsCartOpen={setIsCartOpen}
        cartItems={cartItems}
      />

      {/* ── LUXURY PAGE HERO BANNER ── */}
      <section className="relative py-10 md:py-16 border-b border-[#584738]/12 text-center overflow-hidden">
        <PalaceSilhouette />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <span className="text-[10px] md:text-[11px] tracking-[0.25em] text-[#584738]/60 uppercase font-sans mb-2.5 block font-medium">
            HOUSE OF KARNNIKARAA • JAIPUR
          </span>
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-serif font-normal text-[#584738] tracking-wide mb-3">
            {activeTitle}
          </h1>
          
          {/* Gold Ornamental Line */}
          <div className="flex items-center justify-center gap-3 my-3.5">
            <div className="w-10 h-px bg-[#B59E7D]/40" />
            <div className="w-1.5 h-1.5 rotate-45 bg-[#B59E7D]" />
            <div className="w-10 h-px bg-[#B59E7D]/40" />
          </div>

          <p className="text-xs md:text-sm text-[#584738]/75 font-sans tracking-wide max-w-lg mx-auto font-light leading-relaxed px-2">
            {activeTitle === 'Shipping Policy' && 'Handcrafted with patience & precision — every creation is prepared with care before beginning its journey to you.'}
            {activeTitle === 'Exchange, Return & Refund Policy' && 'Guidelines for hassle-free returns, exchanges, and our mandatory unboxing video requirement.'}
            {activeTitle === 'Terms & Conditions' && 'Terms governing your purchases, handcrafted artisanal features, and legal jurisdiction.'}
            {activeTitle === 'Privacy Policy' && 'How House of Karnnikaraa protects your personal data, transaction security, and privacy.'}
          </p>
        </div>
      </section>

      {/* ── NAVIGATION TABS BAR (Phone View Swipeable Pills & Desktop Tab Row) ── */}
      <div className="sticky top-[65px] z-30 bg-[#F1EADA]/95 backdrop-blur-md border-b border-[#584738]/10 shadow-xs">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="flex items-center justify-start md:justify-center gap-2 sm:gap-4 overflow-x-auto no-scrollbar scrollbar-none py-3 px-1">
            {policiesList.map((pol) => {
              const isActive = pol.name === activeTitle;
              return (
                <button
                  key={pol.id}
                  onClick={() => handleTabClick(pol)}
                  className={`flex items-center gap-2 text-xs tracking-[0.14em] uppercase font-sans whitespace-nowrap px-4 py-2 rounded-full transition-all duration-200 focus:outline-none border ${
                    isActive
                      ? 'bg-[#584738] text-[#F5E6D8] border-[#584738] shadow-xs font-semibold'
                      : 'bg-[#E5DAC6]/60 text-[#584738]/80 border-[#B59E7D]/30 hover:bg-[#E5DAC6] hover:text-[#584738]'
                  }`}
                >
                  <span className={isActive ? 'text-[#F5E6D8]' : 'text-[#584738]/60'}>{pol.icon}</span>
                  <span>{pol.shortName || pol.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT CONTAINER (Zero Pure White Cards, Pure Brand Palette) ── */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-8 md:py-14 flex-1 w-full">

        {/* ════════════════════════════════════════════════════════════ */}
        {/* 1. SHIPPING POLICY                                           */}
        {/* ════════════════════════════════════════════════════════════ */}
        {activeTitle === 'Shipping Policy' && (
          <div className="flex flex-col gap-6 md:gap-8 animate-fade-in">
            
            {/* Quote Card */}
            <div className="bg-[#EAE0CE] border border-[#B59E7D]/40 rounded-lg p-5 sm:p-7 text-center relative shadow-xs">
              <span className="text-3xl font-serif text-[#B59E7D] leading-none block mb-1">“</span>
              <p className="font-serif text-base sm:text-lg md:text-xl text-[#584738] italic font-normal max-w-2xl mx-auto leading-relaxed">
                At House of Karnnikaraa, every creation is thoughtfully handcrafted before it begins its journey to you.
              </p>
            </div>

            {/* Content Cards Grid (Brand Sand Backgrounds) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
              
              {/* Point 1: Dispatch Timeline */}
              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-5 sm:p-6 shadow-xs hover:border-[#B59E7D]/60 transition-all flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center shrink-0">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="text-[10px] tracking-[0.2em] font-bold uppercase text-[#B59E7D]">01 / DISPATCH</span>
                    </div>
                    <span className="px-2.5 py-0.5 rounded-full bg-[#B59E7D]/20 text-[#584738] text-[10px] font-semibold tracking-wider">
                      20–25 Days
                    </span>
                  </div>
                  <h3 className="font-serif text-lg sm:text-xl font-normal text-[#584738] mb-1.5">
                    Dispatch Lead Time
                  </h3>
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    All orders are dispatched within the lead time of <strong className="font-semibold text-[#584738]">20–25 working days</strong> from the date of order confirmation.
                  </p>
                </div>
              </div>

              {/* Point 2: Shipment Confirmation */}
              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-5 sm:p-6 shadow-xs hover:border-[#B59E7D]/60 transition-all flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center shrink-0">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                        </svg>
                      </div>
                      <span className="text-[10px] tracking-[0.2em] font-bold uppercase text-[#B59E7D]">02 / TRACKING</span>
                    </div>
                    <span className="px-2.5 py-0.5 rounded-full bg-[#584738]/10 text-[#584738] text-[10px] font-semibold tracking-wider">
                      Email & SMS
                    </span>
                  </div>
                  <h3 className="font-serif text-lg sm:text-xl font-normal text-[#584738] mb-1.5">
                    Order Confirmation
                  </h3>
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    Once your order has been shipped, you will receive the confirmation via <strong className="font-semibold text-[#584738]">email or SMS</strong>.
                  </p>
                </div>
              </div>

              {/* Point 3: Delivery Timelines */}
              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-5 sm:p-6 shadow-xs hover:border-[#B59E7D]/60 transition-all flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2.5 mb-3.5">
                    <div className="w-9 h-9 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                      </svg>
                    </div>
                    <span className="text-[10px] tracking-[0.2em] font-bold uppercase text-[#B59E7D]">03 / TRANSIT</span>
                  </div>
                  <h3 className="font-serif text-lg sm:text-xl font-normal text-[#584738] mb-1.5">
                    Delivery Timelines
                  </h3>
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    Delivery timelines may vary depending on your location and unforeseen logistical circumstances.
                  </p>
                </div>
              </div>

              {/* Point 4: Delay Communication */}
              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-5 sm:p-6 shadow-xs hover:border-[#B59E7D]/60 transition-all flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2.5 mb-3.5">
                    <div className="w-9 h-9 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                      </svg>
                    </div>
                    <span className="text-[10px] tracking-[0.2em] font-bold uppercase text-[#B59E7D]">04 / UPDATES</span>
                  </div>
                  <h3 className="font-serif text-lg sm:text-xl font-normal text-[#584738] mb-1.5">
                    Proactive Status Communication
                  </h3>
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    In the event of any delay beyond our standard dispatch timeline, our team will keep you informed.
                  </p>
                </div>
              </div>

            </div>

            {/* Customer Support Contact Box */}
            <div className="bg-[#584738] text-[#F1EADA] rounded-lg p-5 sm:p-7 flex flex-col md:flex-row items-center justify-between gap-5 shadow-md border border-[#B59E7D]/30">
              <div className="text-center md:text-left">
                <span className="text-[10px] tracking-[0.25em] text-[#B59E7D] uppercase font-bold block mb-1">
                  CUSTOMER CARE CONCIERGE
                </span>
                <h4 className="font-serif text-xl sm:text-2xl font-normal text-[#F1EADA]">
                  Shipping Queries?
                </h4>
                <p className="text-xs text-[#F1EADA]/80 font-light mt-1">
                  For any shipping-related queries, please reach out to our customer support team.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
                <a
                  href="mailto:houseofkarnnikaraa@gmail.com"
                  className="px-5 py-3 rounded-sm bg-[#B59E7D] text-white hover:bg-[#A38A67] text-[11px] font-medium tracking-[0.15em] uppercase text-center transition-colors shadow-xs w-full sm:w-auto"
                >
                  Email Support
                </a>
                <a
                  href="tel:+919166209215"
                  className="px-5 py-3 rounded-sm border border-[#F1EADA]/30 text-[#F1EADA] hover:bg-[#F1EADA]/10 text-[11px] font-medium tracking-[0.15em] uppercase text-center transition-colors w-full sm:w-auto"
                >
                  Call +91 91662 09215
                </a>
              </div>
            </div>

          </div>
        )}

        {/* ════════════════════════════════════════════════════════════ */}
        {/* 2. EXCHANGE, RETURN & REFUND POLICY                          */}
        {/* ════════════════════════════════════════════════════════════ */}
        {activeTitle === 'Exchange, Return & Refund Policy' && (
          <div className="flex flex-col gap-6 md:gap-8 animate-fade-in">
            
            {/* Intro text card */}
            <div className="bg-[#EAE0CE] border border-[#B59E7D]/40 rounded-lg p-5 sm:p-7 shadow-xs text-center md:text-left flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center shrink-0 hidden sm:flex">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
              </div>
              <div>
                <span className="text-[10px] tracking-[0.25em] text-[#B59E7D] uppercase font-bold block mb-1">
                  CUSTOMER SATISFACTION
                </span>
                <p className="font-serif text-base sm:text-lg md:text-xl text-[#584738] leading-relaxed font-normal">
                  We hope every House of Karnnikaraa creation brings you joy. However, if you wish to request an exchange or return, please review the following policy:
                </p>
              </div>
            </div>

            {/* Core Guidelines */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-5 sm:p-6 shadow-xs flex items-start gap-3.5">
                <div className="w-9 h-9 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center font-serif text-xs font-bold shrink-0 mt-0.5">
                  7D
                </div>
                <div>
                  <h4 className="font-serif text-base sm:text-lg font-normal text-[#584738] mb-1">
                    7 Working Days Initiation
                  </h4>
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    Requests for exchange or return must be initiated within <strong className="font-semibold text-[#584738]">7 working days</strong> of receiving your order.
                  </p>
                </div>
              </div>

              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-5 sm:p-6 shadow-xs flex items-start gap-3.5">
                <div className="w-9 h-9 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center font-serif text-xs font-bold shrink-0 mt-0.5">
                  ✓
                </div>
                <div>
                  <h4 className="font-serif text-base sm:text-lg font-normal text-[#584738] mb-1">
                    Original Condition Eligibility
                  </h4>
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    To be eligible, the product must be <strong className="font-semibold text-[#584738]">unused, unworn, in its original condition</strong>, and returned with all original packaging and accompanying accessories.
                  </p>
                </div>
              </div>
            </div>

            {/* 📹 MANDATORY UNBOXING REQUIREMENT CARD */}
            <div className="bg-[#EBE2D0] border-2 border-[#B59E7D] rounded-lg p-5 sm:p-7 shadow-sm relative overflow-hidden">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 pb-4 border-b border-[#B59E7D]/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#584738] text-[#F1EADA] flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  </div>
                  <div>
                    <span className="text-[10px] tracking-[0.25em] text-[#B59E7D] uppercase font-bold block">
                      MANDATORY VERIFICATION
                    </span>
                    <h3 className="font-serif text-lg sm:text-xl md:text-2xl font-normal text-[#584738]">
                      Mandatory Unboxing Requirement
                    </h3>
                  </div>
                </div>
              </div>

              <p className="text-xs sm:text-sm text-[#584738]/90 mb-4 leading-relaxed font-normal">
                To ensure a smooth and transparent return & exchange process, customers are required to:
              </p>

              <div className="flex flex-col gap-3 mb-5">
                <div className="flex items-start gap-3 bg-[#FAF5EB] p-3.5 rounded-sm border border-[#B59E7D]/30">
                  <span className="w-6 h-6 rounded-full bg-[#584738] text-[#F1EADA] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                    1
                  </span>
                  <p className="text-xs sm:text-sm text-[#584738] leading-relaxed">
                    Record an <strong className="font-semibold text-black">uninterrupted video</strong> of the complete unpacking process, beginning before the package is opened and continuing until the product is fully visible.
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-[#FAF5EB] p-3.5 rounded-sm border border-[#B59E7D]/30">
                  <span className="w-6 h-6 rounded-full bg-[#584738] text-[#F1EADA] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                    2
                  </span>
                  <p className="text-xs sm:text-sm text-[#584738] leading-relaxed">
                    Share this <strong className="font-semibold text-black">original unboxing video</strong> with our team when raising a return or exchange request.
                  </p>
                </div>
              </div>

              <div className="bg-[#E4D5C1] border border-[#B59E7D]/40 rounded-sm p-3.5 flex items-center gap-3">
                <svg className="w-5 h-5 text-[#584738] shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <p className="text-xs sm:text-[13px] text-[#584738] font-medium leading-relaxed">
                  Requests submitted without the required unboxing video may not be eligible for return, exchange, or refund.
                </p>
              </div>
            </div>

            {/* REFUNDS CARD */}
            <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-5 sm:p-7 shadow-xs">
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-[#584738]/12">
                <div className="w-9 h-9 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5h16.5a1.5 1.5 0 011.5 1.5v9.75a1.5 1.5 0 01-1.5 1.5H3.75a1.5 1.5 0 01-1.5-1.5V6a1.5 1.5 0 011.5-1.5z" />
                  </svg>
                </div>
                <h3 className="font-serif text-lg sm:text-xl md:text-2xl font-normal text-[#584738]">
                  Refund Processing
                </h3>
              </div>

              <div className="flex flex-col gap-3.5">
                <div className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rotate-45 bg-[#B59E7D] shrink-0 mt-2" />
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    Once the returned product has been received and successfully inspected by our quality team, the refund will be processed.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rotate-45 bg-[#B59E7D] shrink-0 mt-2" />
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    Approved refunds will be initiated to the <strong className="font-semibold text-[#584738]">original mode of payment</strong> within the applicable banking timelines.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rotate-45 bg-[#B59E7D] shrink-0 mt-2" />
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    Shipping charges, if any, are non-refundable unless the return is due to an incorrect or damaged product delivered by us.
                  </p>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* ════════════════════════════════════════════════════════════ */}
        {/* 3. TERMS & CONDITIONS                                        */}
        {/* ════════════════════════════════════════════════════════════ */}
        {activeTitle === 'Terms & Conditions' && (
          <div className="flex flex-col gap-5 md:gap-7 animate-fade-in">
            
            <div className="bg-[#EAE0CE] border border-[#B59E7D]/40 rounded-lg p-5 sm:p-7 shadow-xs">
              <span className="text-[10px] tracking-[0.25em] text-[#B59E7D] uppercase font-bold block mb-1.5">
                TERMS OF SALE & USE
              </span>
              <p className="font-serif text-base sm:text-lg md:text-xl text-[#584738] leading-relaxed font-normal">
                By placing an order on the House of Karnnikaraa website, you agree to the following terms:
              </p>
            </div>

            <div className="flex flex-col gap-3.5">
              
              {/* Term 01 */}
              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-4 sm:p-5 shadow-xs flex items-start gap-3.5">
                <div className="w-8 h-8 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center font-serif text-xs font-bold shrink-0 mt-0.5">
                  01
                </div>
                <div>
                  <h4 className="font-serif text-base sm:text-lg font-normal text-[#584738] mb-1">
                    Artisanal Handcrafted Variations
                  </h4>
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    All product images are for representation purposes. Minor variations in colour, finish, texture, or handcrafted detailing may occur and are a natural characteristic of artisanal creations.
                  </p>
                </div>
              </div>

              {/* Term 02 */}
              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-4 sm:p-5 shadow-xs flex items-start gap-3.5">
                <div className="w-8 h-8 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center font-serif text-xs font-bold shrink-0 mt-0.5">
                  02
                </div>
                <div>
                  <h4 className="font-serif text-base sm:text-lg font-normal text-[#584738] mb-1">
                    Stock & Availability
                  </h4>
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    Product availability is subject to stock and order confirmation.
                  </p>
                </div>
              </div>

              {/* Term 03 */}
              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-4 sm:p-5 shadow-xs flex items-start gap-3.5">
                <div className="w-8 h-8 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center font-serif text-xs font-bold shrink-0 mt-0.5">
                  03
                </div>
                <div>
                  <h4 className="font-serif text-base sm:text-lg font-normal text-[#584738] mb-1">
                    Order Processing & Cancellations
                  </h4>
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    Once an order has been placed, modifications or cancellations may not be possible after processing has commenced.
                  </p>
                </div>
              </div>

              {/* Term 04 */}
              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-4 sm:p-5 shadow-xs flex items-start gap-3.5">
                <div className="w-8 h-8 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center font-serif text-xs font-bold shrink-0 mt-0.5">
                  04
                </div>
                <div>
                  <h4 className="font-serif text-base sm:text-lg font-normal text-[#584738] mb-1">
                    Order Cancellation Rights
                  </h4>
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    House of Karnnikaraa reserves the right to refuse or cancel any order due to pricing errors, suspected fraudulent activity, or unforeseen circumstances.
                  </p>
                </div>
              </div>

              {/* Term 05 */}
              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-4 sm:p-5 shadow-xs flex items-start gap-3.5">
                <div className="w-8 h-8 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center font-serif text-xs font-bold shrink-0 mt-0.5">
                  05
                </div>
                <div>
                  <h4 className="font-serif text-base sm:text-lg font-normal text-[#584738] mb-1">
                    Customer Information Accuracy
                  </h4>
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    Customers are responsible for providing accurate shipping and contact information. House of Karnnikaraa shall not be liable for delays or delivery issues arising from incorrect details.
                  </p>
                </div>
              </div>

              {/* Term 06 */}
              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-4 sm:p-5 shadow-xs flex items-start gap-3.5">
                <div className="w-8 h-8 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center font-serif text-xs font-bold shrink-0 mt-0.5">
                  06
                </div>
                <div>
                  <h4 className="font-serif text-base sm:text-lg font-normal text-[#584738] mb-1">
                    Governing Law & Legal Jurisdiction
                  </h4>
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    Any disputes arising from purchases made through this website shall be governed by the laws of India and subject to the jurisdiction of the courts in <strong className="font-semibold text-[#584738]">Jaipur, Rajasthan</strong>.
                  </p>
                </div>
              </div>

              {/* Term 07 */}
              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-4 sm:p-5 shadow-xs flex items-start gap-3.5">
                <div className="w-8 h-8 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center font-serif text-xs font-bold shrink-0 mt-0.5">
                  07
                </div>
                <div>
                  <h4 className="font-serif text-base sm:text-lg font-normal text-[#584738] mb-1">
                    Policy Amendments
                  </h4>
                  <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                    House of Karnnikaraa reserves the right to amend these policies and terms at any time with prior notice.
                  </p>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════ */}
        {/* 4. PRIVACY POLICY                                            */}
        {/* ════════════════════════════════════════════════════════════ */}
        {activeTitle === 'Privacy Policy' && (
          <div className="flex flex-col gap-5 md:gap-7 animate-fade-in">
            <div className="bg-[#EAE0CE] border border-[#B59E7D]/40 rounded-lg p-5 sm:p-7 shadow-xs">
              <span className="text-[10px] tracking-[0.25em] text-[#B59E7D] uppercase font-bold block mb-1.5">
                DATA PROTECTION & CONFIDENTIALITY
              </span>
              <p className="font-serif text-base sm:text-lg md:text-xl text-[#584738] leading-relaxed font-normal">
                At House of Karnnikaraa, we respect your privacy and are committed to safeguarding your personal information when you visit or make a purchase from our website.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
              
              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-5 sm:p-6 shadow-xs">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-8 h-8 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    </svg>
                  </div>
                  <span className="text-[10px] tracking-[0.2em] font-bold uppercase text-[#B59E7D]">01 / COLLECTION</span>
                </div>
                <h4 className="font-serif text-base sm:text-lg font-normal text-[#584738] mb-1.5">
                  Information We Collect
                </h4>
                <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                  We collect personal details such as your name, shipping address, email, phone number, and order history necessary to process orders, communicate status, and ensure prompt delivery.
                </p>
              </div>

              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-5 sm:p-6 shadow-xs">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-8 h-8 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751A11.959 11.959 0 0112 2.714z" />
                    </svg>
                  </div>
                  <span className="text-[10px] tracking-[0.2em] font-bold uppercase text-[#B59E7D]">02 / PROTECTION</span>
                </div>
                <h4 className="font-serif text-base sm:text-lg font-normal text-[#584738] mb-1.5">
                  Data Usage Guarantee
                </h4>
                <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                  Your details are strictly used to fulfill handcrafted orders, send shipment notifications, and provide support. We never sell, rent, or trade your data to third parties.
                </p>
              </div>

              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-5 sm:p-6 shadow-xs">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-8 h-8 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  </div>
                  <span className="text-[10px] tracking-[0.2em] font-bold uppercase text-[#B59E7D]">03 / SECURITY</span>
                </div>
                <h4 className="font-serif text-base sm:text-lg font-normal text-[#584738] mb-1.5">
                  Secure Electronic Payments
                </h4>
                <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                  All transactions are handled through certified, PCI-compliant payment gateways. House of Karnnikaraa does not store raw credit/debit card numbers or banking passwords.
                </p>
              </div>

              <div className="bg-[#F3EBDD] border border-[#B59E7D]/35 rounded-lg p-5 sm:p-6 shadow-xs">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-8 h-8 rounded-full bg-[#584738]/10 text-[#584738] border border-[#B59E7D]/30 flex items-center justify-center shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0m-9.75 0h9.75" />
                    </svg>
                  </div>
                  <span className="text-[10px] tracking-[0.2em] font-bold uppercase text-[#B59E7D]">04 / EXPERIENCE</span>
                </div>
                <h4 className="font-serif text-base sm:text-lg font-normal text-[#584738] mb-1.5">
                  Cookies & Site Analytics
                </h4>
                <p className="text-xs sm:text-sm text-[#584738]/85 leading-relaxed font-light">
                  We use essential cookies to maintain shopping cart items, remember account preferences, and analyze site performance for an optimal browsing experience.
                </p>
              </div>

            </div>
          </div>
        )}

      </main>

      {/* FOOTER */}
      <Footer setView={setView} setLegalTitle={setLegalTitle} />

      {/* DRAWERS & MODALS */}
      <MenuDrawer
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        setView={setView}
        setIsBookingOpen={setIsBookingOpen}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
        setIsAuthOpen={setIsAuthModalOpen}
      />
      <BookingDrawer
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        bookingStep={bookingStep}
        setBookingStep={setBookingStep}
        bookingForm={bookingForm}
        setBookingForm={setBookingForm}
        cartItems={cartItems}
      />
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
      />
    </div>
  );
}
